import React from "react";
import {
  Box,
  Button,
  Chip,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { getLandingPreviewDevice } from "../previewDevices";
import { VisualColorControl, VisualSliderControl } from "../inspector/VisualSettingControls";

export function PageEditorShell({
  title,
  description,
  device = "desktop",
  saving = false,
  children,
  preview,
  contextual = false,
  inspectorOpen = false,
  onCloseInspector,
  surfaceLabel = "",
}) {
  const previewDevice = getLandingPreviewDevice(device);
  const compactEditor = useMediaQuery("(max-width:1100px)");
  const mobile = useMediaQuery("(max-width:760px)");

  if (contextual) {
    const horizontalGutter = previewDevice.id === "mobile" ? 0 : 32;
    return (
      <>
        <Box
          component="main"
          sx={{
            position: "fixed",
            inset: { xs: "136px 0 0", sm: "88px 0 0" },
            right: 0,
            "@media (min-width: 1101px)": {
              right: inspectorOpen ? "410px" : 0,
            },
            overflow: "auto",
            bgcolor: "#E9ECF0",
            transition: compactEditor ? "none" : "right .18s ease",
          }}
        >
          <Box
            sx={{
              minWidth: `${previewDevice.width + horizontalGutter}px`,
              minHeight: "100%",
              px: previewDevice.id === "mobile" ? 0 : 2,
              py: 1.5,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: `${previewDevice.width}px`, minHeight: 28, mb: 1, px: 0.25, color: "rgba(25,36,47,.64)" }}
            >
              <Typography variant="caption" sx={{ fontWeight: 900 }}>
                {surfaceLabel || "Página"} · {previewDevice.label} · {previewDevice.width}px
              </Typography>
              {saving ? (
                <Typography variant="caption" role="status" aria-live="polite" sx={{ fontWeight: 850 }}>
                  Guardando borrador…
                </Typography>
              ) : null}
            </Stack>
            <Box
              data-page-editor-preview-device={previewDevice.id}
              sx={{
                width: `${previewDevice.width}px`,
                bgcolor: "#fff",
                borderRadius: previewDevice.id === "mobile" ? 2.5 : previewDevice.id === "tablet" ? 2 : 1.5,
                outline: "1px solid rgba(25,36,47,.12)",
                boxShadow: previewDevice.id === "desktop"
                  ? "0 14px 42px rgba(25,36,47,.12)"
                  : "0 18px 48px rgba(25,36,47,.18)",
                overflow: "hidden",
              }}
            >
              {preview}
            </Box>
          </Box>
        </Box>

        <Drawer
          anchor={compactEditor ? "bottom" : "right"}
          variant={compactEditor ? "temporary" : "persistent"}
          open={inspectorOpen}
          onClose={saving ? undefined : onCloseInspector}
          PaperProps={{
            sx: compactEditor
              ? {
                  width: "100%",
                  maxWidth: "100%",
                  maxHeight: mobile ? "88vh" : "74vh",
                  borderRadius: "20px 20px 0 0",
                  "& .MuiButton-root": { minHeight: 44 },
                  "& .MuiIconButton-root": { minWidth: 44, minHeight: 44 },
                  "& .MuiInputBase-root": { minHeight: 44 },
                  "& .MuiFormControlLabel-root": { minHeight: 44 },
                  "& input[type='color']": { minWidth: 48, minHeight: 44 },
                }
              : { width: 410, maxWidth: "92vw", top: 88, height: "calc(100% - 88px)" },
          }}
        >
          <Stack sx={{ minHeight: 0, height: compactEditor ? (mobile ? "88vh" : "74vh") : "100%" }}>
            <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ px: 2, py: 1.6, borderBottom: "1px solid rgba(25,36,47,.09)" }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" sx={{ color: "#F35A3A", fontWeight: 950, letterSpacing: .7 }}>
                  {surfaceLabel || "Página"}
                </Typography>
                <Typography sx={{ mt: 0.2, fontWeight: 950, color: "#19242F", lineHeight: 1.15 }}>
                  {title}
                </Typography>
                {description ? (
                  <Typography variant="body2" sx={{ mt: 0.35, color: "rgba(25,36,47,.62)" }}>
                    {description}
                  </Typography>
                ) : null}
              </Box>
              <IconButton aria-label="Cerrar editor" onClick={onCloseInspector} disabled={saving}>
                <CloseRoundedIcon />
              </IconButton>
            </Stack>
            <Box sx={{ p: 2, overflowY: "auto", flex: 1 }}>
              <Stack spacing={2}>{children}</Stack>
            </Box>
          </Stack>
        </Drawer>
      </>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        position: "fixed",
        inset: { xs: "136px 0 0", sm: "88px 0 0" },
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "390px minmax(0,1fr)" },
        gridAutoRows: { xs: "max-content", lg: "auto" },
        bgcolor: "#E9ECF0",
        overflowX: "hidden",
        overflowY: { xs: "auto", lg: "hidden" },
      }}
    >
      <Box
        component="aside"
        sx={{
          p: 2,
          overflowY: { xs: "visible", lg: "auto" },
          borderRight: { lg: "1px solid rgba(25,36,47,.12)" },
          bgcolor: "#F8F9FB",
        }}
      >
        <Stack spacing={0.7} sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="h6" sx={{ fontWeight: 950, color: "#19242F" }}>
              {title}
            </Typography>
            {saving ? (
              <Typography
                variant="caption"
                role="status"
                aria-live="polite"
                sx={{ fontWeight: 850, color: "rgba(25,36,47,.56)" }}
              >
                Guardando…
              </Typography>
            ) : null}
          </Stack>
          {description ? (
            <Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)" }}>
              {description}
            </Typography>
          ) : null}
        </Stack>
        <Stack spacing={2}>{children}</Stack>
      </Box>

      <Box
        sx={{
          overflowX: "auto",
          overflowY: { xs: "hidden", lg: "auto" },
          p: { xs: 1.25, sm: 2.5 },
        }}
      >
        <Box sx={{ minWidth: `${previewDevice.width}px`, width: "fit-content", mx: "auto" }}>
          <Typography
            variant="caption"
            sx={{ display: "block", mb: 1, px: 0.25, color: "rgba(25,36,47,.62)", fontWeight: 900 }}
          >
            {previewDevice.label} · {previewDevice.width}px · vista de configuración
          </Typography>
          <Box
            data-page-editor-preview-device={previewDevice.id}
            sx={{
              width: `${previewDevice.width}px`,
              bgcolor: "#fff",
              borderRadius: previewDevice.id === "mobile" ? 2.5 : 1.5,
              outline: "1px solid rgba(25,36,47,.12)",
              boxShadow: "0 16px 46px rgba(25,36,47,.14)",
              overflow: "hidden",
            }}
          >
            {preview}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}


export function PagePreviewTarget({
  targetId,
  activeTarget,
  onSelect,
  label,
  children,
  sx = {},
}) {
  const selected = activeTarget === targetId;
  const targetLabel = label || "elemento";
  const select = React.useCallback((event) => {
    event?.stopPropagation?.();
    onSelect?.(targetId);
  }, [onSelect, targetId]);

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`Editar ${String(targetLabel).toLowerCase()}`}
      aria-pressed={selected}
      data-page-editor-target={targetId}
      onClick={select}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        select(event);
      }}
      sx={{
        position: "relative",
        borderRadius: 1.5,
        outline: selected ? "3px solid rgba(35,111,231,.78)" : "2px solid transparent",
        outlineOffset: 3,
        cursor: "pointer",
        transition: "outline-color .16s ease, box-shadow .16s ease",
        "&:hover": {
          outlineColor: selected ? "rgba(35,111,231,.78)" : "rgba(35,111,231,.42)",
          boxShadow: "0 0 0 4px rgba(35,111,231,.06)",
        },
        "&:focus-visible": {
          outlineColor: "rgba(35,111,231,.92)",
          boxShadow: "0 0 0 4px rgba(35,111,231,.14)",
        },
        "& [data-preview-passive='true']": { pointerEvents: "none" },
        "&:hover > .page-editor-target-chip, &:focus-visible > .page-editor-target-chip": {
          opacity: 1,
          transform: "translateY(0)",
        },
        ...sx,
      }}
    >
      <Chip
        className="page-editor-target-chip"
        size="small"
        label={`Editar ${targetLabel}`}
        sx={{
          position: "absolute",
          zIndex: 5,
          top: 6,
          right: 6,
          height: 22,
          pointerEvents: "none",
          opacity: selected ? 1 : 0,
          transform: selected ? "translateY(0)" : "translateY(-3px)",
          transition: "opacity .16s ease, transform .16s ease",
          bgcolor: "rgba(25,36,47,.92)",
          color: "#fff",
          fontWeight: 850,
        }}
      />
      {children}
    </Box>
  );
}

export function PageHiddenTarget({ targetId, activeTarget, onSelect, label }) {
  return (
    <PagePreviewTarget targetId={targetId} activeTarget={activeTarget} onSelect={onSelect} label={label}>
      <Box
        data-preview-passive="true"
        sx={{
          minHeight: 48,
          border: "1px dashed rgba(25,36,47,.28)",
          borderRadius: 1.7,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "rgba(255,255,255,.52)",
        }}
      >
        <Chip size="small" variant="outlined" label={`${label} oculto`} />
      </Box>
    </PagePreviewTarget>
  );
}

export function PageEditorContextIntro({ children = "Tocá cualquier elemento del preview para editar sólo ese componente." }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.3, borderRadius: 2.3, bgcolor: "rgba(35,111,231,.035)" }}>
      <Typography variant="body2" sx={{ fontWeight: 850, color: "#19242F" }}>
        {children}
      </Typography>
    </Paper>
  );
}

export function PageEditorBackButton({ onClick, label = "Volver a página", startIcon = null }) {
  return (
    <Button
      variant="text"
      startIcon={startIcon}
      onClick={onClick}
      sx={{ justifyContent: "flex-start", textTransform: "none", fontWeight: 900 }}
    >
      {label}
    </Button>
  );
}

export function EditorGroup({ title, description, children }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.6, borderRadius: 2.5 }}>
      <Typography sx={{ fontWeight: 950, color: "#19242F" }}>{title}</Typography>
      {description ? (
        <Typography variant="caption" sx={{ display: "block", mt: 0.35, mb: 1.4, color: "rgba(25,36,47,.58)" }}>
          {description}
        </Typography>
      ) : (
        <Box sx={{ height: 8 }} />
      )}
      <Stack spacing={1.4}>{children}</Stack>
    </Paper>
  );
}

export function TextSetting({ label, value, onChange, multiline = false, minRows = 2, helperText = "" }) {
  return (
    <TextField
      size="small"
      fullWidth
      label={label}
      value={value ?? ""}
      onChange={(event) => onChange(event.target.value)}
      multiline={multiline}
      minRows={multiline ? minRows : undefined}
      helperText={helperText || undefined}
    />
  );
}

export function NumberSetting({ label, value, onChange, min, max, step = 1, helperText = "" }) {
  const parseNumber = React.useCallback((rawValue) => {
    if (rawValue === "" || rawValue == null) return null;
    const numericValue = Number(rawValue);
    return Number.isFinite(numericValue) ? numericValue : null;
  }, []);

  const isValidValue = React.useCallback((numericValue) => {
    if (!Number.isInteger(numericValue)) return false;
    const minimum = Number(min);
    const maximum = Number(max);
    if (Number.isFinite(minimum) && numericValue < minimum) return false;
    if (Number.isFinite(maximum) && numericValue > maximum) return false;
    return true;
  }, [max, min]);

  const initialValue = parseNumber(value);
  const initialValidValue = isValidValue(initialValue) ? initialValue : null;
  const [draftValue, setDraftValue] = React.useState(
    initialValidValue == null ? "" : String(initialValidValue)
  );
  const hasTransientDraftRef = React.useRef(false);
  const lastValidValueRef = React.useRef(initialValidValue);
  const committedValueRef = React.useRef(initialValidValue);

  React.useEffect(() => {
    const nextExternalValue = parseNumber(value);
    const nextValidValue = isValidValue(nextExternalValue) ? nextExternalValue : null;

    if (nextValidValue != null) {
      lastValidValueRef.current = nextValidValue;
      committedValueRef.current = nextValidValue;
    }

    if (!hasTransientDraftRef.current) {
      setDraftValue(nextValidValue == null ? "" : String(nextValidValue));
    }
  }, [isValidValue, parseNumber, value]);

  const commitValue = React.useCallback((nextValue) => {
    hasTransientDraftRef.current = false;
    lastValidValueRef.current = nextValue;
    setDraftValue(String(nextValue));

    if (committedValueRef.current === nextValue) return;
    committedValueRef.current = nextValue;
    onChange(nextValue);
  }, [onChange]);

  const restoreLastValidValue = React.useCallback(() => {
    hasTransientDraftRef.current = false;
    const fallbackValue = lastValidValueRef.current;
    setDraftValue(fallbackValue == null ? "" : String(fallbackValue));
  }, []);

  const finalizeDraft = React.useCallback((rawValue) => {
    const numericValue = parseNumber(rawValue);
    if (numericValue == null || !Number.isInteger(numericValue)) {
      restoreLastValidValue();
      return;
    }

    const minimum = Number(min);
    const maximum = Number(max);
    let normalizedValue = numericValue;
    if (Number.isFinite(minimum)) normalizedValue = Math.max(minimum, normalizedValue);
    if (Number.isFinite(maximum)) normalizedValue = Math.min(maximum, normalizedValue);

    commitValue(normalizedValue);
  }, [commitValue, max, min, parseNumber, restoreLastValidValue]);

  return (
    <TextField
      size="small"
      fullWidth
      type="number"
      label={label}
      value={draftValue}
      onChange={(event) => {
        const rawValue = event.target.value;
        const numericValue = parseNumber(rawValue);
        setDraftValue(rawValue);

        if (!isValidValue(numericValue)) {
          hasTransientDraftRef.current = true;
          return;
        }

        hasTransientDraftRef.current = false;
        lastValidValueRef.current = numericValue;
        if (committedValueRef.current === numericValue) return;
        committedValueRef.current = numericValue;
        onChange(numericValue);
      }}
      onBlur={(event) => finalizeDraft(event.target.value)}
      onKeyDown={(event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        finalizeDraft(event.target.value);
      }}
      inputProps={{ min, max, step }}
      helperText={helperText || undefined}
    />
  );
}

export function ToggleSetting({ label, checked, onChange, helperText = "" }) {
  return (
    <Box>
      <FormControlLabel
        control={<Switch checked={checked !== false} onChange={(event) => onChange(event.target.checked)} />}
        label={label}
        sx={{ m: 0 }}
      />
      {helperText ? (
        <Typography variant="caption" sx={{ display: "block", pl: 6.2, color: "rgba(25,36,47,.56)" }}>
          {helperText}
        </Typography>
      ) : null}
    </Box>
  );
}

export function SegmentedChoiceSetting({ label, value, onChange, options = [], helperText = "", disabled = false }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 900, color: "#19242F" }}>
        {label}
      </Typography>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={value}
        disabled={disabled}
        aria-label={label}
        onChange={(_, nextValue) => {
          if (nextValue === null || nextValue === undefined) return;
          onChange(nextValue);
        }}
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.65,
          "& .MuiToggleButtonGroup-grouped": {
            m: "0 !important",
            border: "1px solid rgba(25,36,47,.14) !important",
            borderRadius: "10px !important",
          },
          "& .MuiToggleButton-root": {
            flex: "1 1 132px",
            minWidth: 0,
            minHeight: 44,
            px: 1.2,
            textTransform: "none",
            fontWeight: 850,
            lineHeight: 1.2,
          },
        }}
      >
        {options.map((option) => (
          <ToggleButton key={option.value} value={option.value}>
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {helperText ? (
        <Typography variant="caption" sx={{ display: "block", mt: 0.55, color: "rgba(25,36,47,.56)" }}>
          {helperText}
        </Typography>
      ) : null}
    </Box>
  );
}

export function SliderSetting({
  label,
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  unit = "",
  helperText = "",
  disabled = false,
  valueLabelFormatter,
}) {
  return (
    <VisualSliderControl
      label={label}
      value={value}
      min={min}
      max={max}
      step={step}
      unit={unit}
      disabled={disabled}
      helperText={helperText}
      valueLabelFormatter={valueLabelFormatter}
      onChange={onChange}
    />
  );
}

export function SelectSetting({ label, value, onChange, options = [], helperText = "" }) {
  return (
    <FormControl size="small" fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {helperText ? (
        <Typography variant="caption" sx={{ mt: 0.5, color: "rgba(25,36,47,.56)" }}>
          {helperText}
        </Typography>
      ) : null}
    </FormControl>
  );
}

export function ColorSetting({
  label,
  value,
  onChange,
  helperText = "",
  fallback = "#000000",
  inheritedLabel = "Heredado",
  allowTransparent = true,
  allowOpacity = true,
}) {
  return (
    <VisualColorControl
      label={label}
      value={value}
      fallback={fallback}
      helperText={helperText}
      inheritedLabel={inheritedLabel}
      allowTransparent={allowTransparent}
      allowOpacity={allowOpacity}
      onChange={onChange}
      onReset={() => onChange("")}
    />
  );
}

